const ABSOLUTE_SCHEME = /^[a-z][a-z0-9+.-]*:/i;
const SAFE_ABSOLUTE_SCHEME = /^https?:/i;

/** Relative URLs are fine; an absolute URL must be http(s) — `javascript:`/`data:`/anything else is rejected. */
function isSafeUrl(url: string): boolean {
  if (!ABSOLUTE_SCHEME.test(url)) return true;
  return SAFE_ABSOLUTE_SCHEME.test(url);
}

const INLINE_PATTERN = /`([^`]+)`|\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*|\*([^*]+)\*/g;

function appendInline(parent: Node, text: string): void {
  let lastIndex = 0;
  INLINE_PATTERN.lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = INLINE_PATTERN.exec(text))) {
    if (match.index > lastIndex) {
      parent.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
    }

    const [, code, linkLabel, linkUrl, bold, italic] = match;
    if (code !== undefined) {
      const el = document.createElement("code");
      el.textContent = code;
      parent.appendChild(el);
    } else if (linkLabel !== undefined && linkUrl !== undefined) {
      if (isSafeUrl(linkUrl)) {
        const a = document.createElement("a");
        a.href = linkUrl;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        a.textContent = linkLabel;
        parent.appendChild(a);
      } else {
        parent.appendChild(document.createTextNode(linkLabel));
      }
    } else if (bold !== undefined) {
      const el = document.createElement("strong");
      el.textContent = bold;
      parent.appendChild(el);
    } else if (italic !== undefined) {
      const el = document.createElement("em");
      el.textContent = italic;
      parent.appendChild(el);
    }

    lastIndex = INLINE_PATTERN.lastIndex;
  }

  if (lastIndex < text.length) {
    parent.appendChild(document.createTextNode(text.slice(lastIndex)));
  }
}

function appendLines(parent: Node, lines: string[]): void {
  lines.forEach((line, index) => {
    appendInline(parent, line);
    if (index < lines.length - 1) parent.appendChild(document.createElement("br"));
  });
}

function isListBlock(lines: string[]): boolean {
  return lines.length > 0 && lines.every((line) => /^\s*[-*]\s+/.test(line));
}

/**
 * Builds DOM nodes directly — paragraphs, bold, italic, inline code, lists,
 * links, line breaks — with no `innerHTML` anywhere, so there's no HTML
 * string for `<script>`/`onerror=`/anything else to hide in: any literal
 * `<...>` in the source text becomes a text node, never a parsed element.
 * See PLAN.md "Markdown without XSS" and test 10.
 */
export function renderMarkdown(text: string): DocumentFragment {
  const fragment = document.createDocumentFragment();
  const blocks = text.split(/\n{2,}/);

  for (const block of blocks) {
    if (block.trim() === "") continue;
    const lines = block.split("\n");

    if (isListBlock(lines)) {
      const ul = document.createElement("ul");
      for (const line of lines) {
        const li = document.createElement("li");
        appendInline(li, line.replace(/^\s*[-*]\s+/, ""));
        ul.appendChild(li);
      }
      fragment.appendChild(ul);
    } else {
      const p = document.createElement("p");
      appendLines(p, lines);
      fragment.appendChild(p);
    }
  }

  return fragment;
}
